import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from 'antd';
import { setIsApplePayAvailable } from '../../../../slices/GooglePaySlice';
import Loading from '../../loadingScreen/Loading';
import ErrorModal from '../../modals/errorModal/ErrorModal';
import { InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from 'react-i18next';
import { isEmpty } from '../../../../Utils/utils';

const ApplePayComponent = ({
    st,
    formRef,
    jwt,
    handleInitiatePayment,
    showErrorModal,
    setShowErrorModal,
    isFormSubmitting,
    setIsFormSubmitting,
    clearFormAction,
    handleInitiatePaymentAnalytics,
    walletSurcharges = [],
    totalPaymentSummaryValue,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const initAttempted = useRef(false);
    const observerRef = useRef(null);
    const timerRef = useRef(null);

    const [isButtonReady, setIsButtonReady] = useState(false);

    const isApplePayAvailable = useSelector((state) => state.gpayDetails?.isApplePayAvailable);
    const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);

    const handleInitiatePaymentRef = useRef(handleInitiatePayment);
    useEffect(() => {
        handleInitiatePaymentRef.current = handleInitiatePayment;
    }, [handleInitiatePayment]);

    const paymentDetails = sessionDetails.paymentDetails;
    const applePayConfig = sessionDetails.configs?.paymentMethods?.find(
        (el) => el.type?.toLowerCase() === 'wallet' && el.brand?.toLowerCase() === 'applepay'
    );

    const applePayAppData = applePayConfig?.additionalData;
    const supportedNetworks = !isEmpty(applePayAppData?.supportedNetworks) ? JSON.parse(JSON.stringify(applePayAppData?.supportedNetworks)) : [];
    const merchantCapabilities = !isEmpty(applePayAppData?.merchantCapabilities) ? JSON.parse(JSON.stringify(applePayAppData?.merchantCapabilities)) : [];

    const merchantId = applePayAppData?.siteReference;
    const baseAmount = Number(paymentDetails?.money?.amount);
    const surchargeTotal = walletSurcharges.reduce((sum, s) => sum + Number(s.calculatedSurchargeFee || 0), 0);
    const computedTotal = walletSurcharges.length > 0 ? baseAmount + surchargeTotal : null;
    const totalPrice = Number(computedTotal ?? totalPaymentSummaryValue ?? baseAmount ?? 0).toFixed(2);

    const countryCode = paymentDetails?.context?.countryCode;
    const currency = paymentDetails?.money?.currencyCode;

    const lineItems = walletSurcharges.length > 0 ? [
        {
            label: 'Subtotal',
            amount: baseAmount.toFixed(2),
            type: 'final',
        },
        ...walletSurcharges.map((s) => ({
            label: s.surchargeDetails?.title ?? 'Surcharge',
            amount: Number(s.calculatedSurchargeFee).toFixed(2),
            type: 'final',
        })),
    ] : undefined;

    // Refs so the ApplePaySession patch always reads latest values, not stale ones
    // const lineItemsRef = useRef(lineItems);
    // const totalPriceRef = useRef(totalPrice);
    // const totalLabelRef = useRef(applePayAppData?.merchantName);
    // useEffect(() => { lineItemsRef.current = lineItems; }, [lineItems]);
    // useEffect(() => { totalPriceRef.current = totalPrice; }, [totalPrice]);
    // useEffect(() => { totalLabelRef.current = applePayAppData?.merchantName; }, [applePayAppData?.merchantName]);

    // TrustPayments builds its own ApplePayPaymentRequest from the JWT and drops
    // lineItems. We wrap the ApplePaySession constructor with a Proxy to inject
    // them right before the sheet is created. (Apple Pay's equivalent of the
    // Google Pay loadPaymentData patch.)
    // const apPatchRef = useRef(null);

    // Reset initAttempted when st is cleared so a new instance triggers fresh init
    useEffect(() => {
        if (!st) {
            initAttempted.current = false;
        }
    }, [st]);

    const rejectPayment = (message) => Promise.resolve({
        transactionState: 'ERROR',
        error: { message: message || 'An error occurred, please try again.' }
    });

    const acceptPayment = () => Promise.resolve({ transactionState: 'SUCCESS' });

    async function onPaymentAuthorized(paymentData, actions) {
        try {
            const result = await handleInitiatePaymentRef.current("wallet/applepay");

            if (result?.success && result?.formActionUrl) {
                if (formRef.current) formRef.current.action = result.formActionUrl;
                return acceptPayment();
            } else {
                setIsFormSubmitting(false);
                // Retry limit is shown by the global modal in BoxPayCheckout.
                if (!result?.retryLimitReached) setShowErrorModal(true);
                handleInitiatePaymentAnalytics("ApplePay/paymentInitiateError", JSON.stringify(result));
                return rejectPayment(result?.message || "Something Went Wrong, Please Try Again");
            }
        } catch (err) {
            handleInitiatePaymentAnalytics("ApplePay/paymentInitiateError", err ? JSON.stringify(err) : "An error occurred during payment authorization");
            setShowErrorModal(true);
            setIsFormSubmitting(false);
            return rejectPayment();
        }
    }

    useEffect(() => {
        if (!st || !jwt || initAttempted.current) return;

        timerRef.current = setTimeout(() => {
            const container = document.getElementById('st-apple-pay');

            if (!container) return;

            initAttempted.current = true;

            try {
                st.on('paymentCompleted', (data) => {
                    setIsFormSubmitting(true);
                });

                st.on('paymentFailed', (data) => {
                    if (formRef.current) {
                        const currentAction = formRef.current.getAttribute('action');
                        if (currentAction && currentAction !== "" && currentAction !== window.location.href) {
                            const jwtInput = document.createElement('input');
                            jwtInput.type = 'hidden';
                            jwtInput.name = 'jwt';
                            jwtInput.value = jwt;
                            formRef.current.appendChild(jwtInput);
                            formRef.current.submit();
                        } else {
                            setIsFormSubmitting(false);
                        }
                    }
                });

                st.on('paymentCanceled', (error) => {
                    setIsFormSubmitting(false);
                    handleInitiatePaymentAnalytics("ApplePay/paymentCanceled", error ? JSON.stringify(error) : "Payment was canceled by the user.");
                });

                st.on('applePayNotAvailable', () => {
                    dispatch(setIsApplePayAvailable(false));
                });

                st.ApplePay({
                    buttonStyle: 'white-outline',
                    buttonText: 'plain',
                    merchantId: merchantId,
                    buttonPlacement: 'st-apple-pay',
                    paymentRequest: {
                        countryCode: countryCode,
                        currencyCode: currency,
                        merchantCapabilities: merchantCapabilities,
                        supportedNetworks: supportedNetworks,
                        total: {
                            label: applePayAppData?.merchantName,
                            amount: totalPrice,
                        },
                        ...(lineItems && { lineItems }),
                    },
                    callbacks: {
                        onPaymentAuthorized: onPaymentAuthorized
                    }
                });

                observerRef.current = new MutationObserver(() => {
                    if (container.children.length > 0) {
                        setIsButtonReady(true);
                        observerRef.current?.disconnect();

                        // TrustPayments strips lineItems internally — wrap the
                        // ApplePaySession constructor to inject them before the
                        // sheet is created.
                        // try {
                        //     const OrigApplePaySession = window.ApplePaySession;
                        //     if (OrigApplePaySession && !apPatchRef.current && !OrigApplePaySession.__bpPatched) {
                        //         apPatchRef.current = OrigApplePaySession;
                        //         const Patched = new Proxy(OrigApplePaySession, {
                        //             construct(target, args) {
                        //                 try {
                        //                     const request = args[1];
                        //                     const items = lineItemsRef.current;
                        //                     if (request && items?.length) {
                        //                         request.lineItems = items;
                        //                         request.total = {
                        //                             label: totalLabelRef.current,
                        //                             amount: totalPriceRef.current,
                        //                             type: 'final',
                        //                         };
                        //                     }
                        //                 } catch (_) {
                        //                     // injection failed — sheet opens without line items
                        //                 }
                        //                 return Reflect.construct(target, args, target);
                        //             },
                        //         });
                        //         Patched.__bpPatched = true;
                        //         window.ApplePaySession = Patched;
                        //     }
                        // } catch (_) {
                        //     // patch failed — payment continues without surcharge display
                        // }
                    }
                });
                observerRef.current.observe(container, { childList: true, subtree: true });

            } catch (err) {
                handleInitiatePaymentAnalytics("ApplePay/paymentCanceled", err ? JSON.stringify(err) : "An error occurred during Apple Pay initialization.");
            }
        }, 800);

        return () => {
            clearTimeout(timerRef.current);
            observerRef.current?.disconnect();
            // if (apPatchRef.current) {
            //     window.ApplePaySession = apPatchRef.current;
            //     apPatchRef.current = null;
            // }
        };
    }, [st, jwt]);

    return (
        <div className="payment-container">
            {isFormSubmitting && (
                <Loading text={t("PLEASE_WAIT_PROCESSING_PAYMENT")} />
            )}

            <ErrorModal
                title={t("PAYMENT_FAILED")}
                desc={t("PAYMENT_RETRY_NOTICE")}
                showModal={showErrorModal}
                primaryOnClick={() => setShowErrorModal(false)}
                icon={<InfoCircleOutlined style={{ color: "#FF4D4F" }} />}
            />

            {isApplePayAvailable && (
                <>
                    {!isButtonReady && (
                        <Skeleton.Button active style={{ width: "100%", height: 40 }} shape="default" />
                    )}
                    <div style={{ display: isButtonReady ? 'block' : 'none' }}>
                        <div id="st-apple-pay" onClick={() => {
                            setIsFormSubmitting(true)
                            clearFormAction()
                            handleInitiatePaymentAnalytics("ApplePay")
                        }} className='payment-button-box' />
                    </div>
                </>
            )}
        </div>
    );
};

export default ApplePayComponent;
